import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Card,
  CardContent,
  Grid,
  IconButton,
  Divider,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Collapse,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  InputAdornment,
  Paper,
  Drawer
} from '@mui/material';
import {
  Add,
  Remove,
  Delete,
  Search,
  ShoppingCart,
  Receipt,
  ViewModule,
  ViewList,
  FilterList,
  ExpandLess,
  Clear,
  Close
} from '@mui/icons-material';
import AdminPageHeader from '../components/AdminPageHeader';
import productService from '../services/productService';
import saleService from '../services/saleService';
import paymentService from '../services/paymentService';
import uploadService from '../services/uploadService';
import ReceiptModal from '../components/ReceiptModal';
import './POS.css';

const currencyFormatter = new Intl.NumberFormat('es-AR', {
  style: 'currency',
  currency: 'ARS',
  minimumFractionDigits: 2
});
const formatCurrency = value => currencyFormatter.format(Number(value) || 0);

const POS = () => {
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [openCheckout, setOpenCheckout] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [processingPayment, setProcessingPayment] = useState(false);
  const [openReceipt, setOpenReceipt] = useState(false);
  const [completedSale, setCompletedSale] = useState(null);
  const [mobileCartOpen, setMobileCartOpen] = useState(false);

  // Estados para vistas y filtros
  const [viewMode, setViewMode] = useState(() => (
    localStorage.getItem('posViewMode') === 'list' ? 'list' : 'grid-large'
  ));
  const [showFilters, setShowFilters] = useState(false);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [filters, setFilters] = useState({
    category: '',
    brand: '',
    minPrice: '',
    maxPrice: '',
    minStock: 0
  });

  const [checkoutData, setCheckoutData] = useState({
    customerName: '',
    customerDocument: '',
    customerEmail: '',
    paymentMethod: 'efectivo',
    tipoComprobante: 0, // Recibo/Ticket por defecto
    discount: 0,
    discountType: 'percentage', // 'percentage' o 'fixed'
    notes: ''
  });

  useEffect(() => {
    loadCategoriesAndBrands();
  }, []);

  useEffect(() => {
    loadProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, filters]);

  const loadCategoriesAndBrands = async () => {
    const [categoriesResult, brandsResult] = await Promise.all([
      productService.getCategories(),
      productService.getBrands()
    ]);
    
    if (categoriesResult.success) {
      setCategories(categoriesResult.data);
    }
    
    if (brandsResult.success) {
      setBrands(brandsResult.data);
    }
  };

  const loadProducts = async () => {
    setLoading(true);
    
    const params = {
      search: search || undefined,
      active: true,
      categoryId: filters.category || undefined,
      brandId: filters.brand || undefined
    };
    
    const result = await productService.getAll(params);
    
    if (result.success) {
      let filteredProducts = result.data.filter(p => p.stock > filters.minStock);
      
      // Filtrar por rango de precios
      if (filters.minPrice) {
        filteredProducts = filteredProducts.filter(p => p.price >= parseFloat(filters.minPrice));
      }
      if (filters.maxPrice) {
        filteredProducts = filteredProducts.filter(p => p.price <= parseFloat(filters.maxPrice));
      }
      
      setProducts(filteredProducts);
    }
    setLoading(false);
  };

  const handleViewModeChange = (event, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode);
      localStorage.setItem('posViewMode', newMode);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setFilters({
      category: '',
      brand: '',
      minPrice: '',
      maxPrice: '',
      minStock: 0
    });
  };

  const hasActiveFilters = () => {
    return filters.category || filters.brand || filters.minPrice || filters.maxPrice || filters.minStock > 0;
  };

  // Filtros avanzados activos (la categoría vive en la tira de chips, no cuenta acá)
  const advancedFilterCount = [
    filters.brand,
    filters.minPrice,
    filters.maxPrice,
    filters.minStock > 0 ? filters.minStock : ''
  ].filter(Boolean).length;

  const activeCategories = categories.filter(category => category.isActive !== false);

  const addToCart = (product) => {
    const existingItem = cart.find(item => item.productId === product.id);
    
    if (existingItem) {
      if (existingItem.quantity >= product.stock) {
        setMessage({ type: 'warning', text: 'No hay más stock disponible' });
        return;
      }
      setCart(cart.map(item =>
        item.productId === product.id
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setCart([...cart, {
        productId: product.id,
        name: product.name,
        price: product.price,
        quantity: 1,
        maxStock: product.stock
      }]);
    }
  };

  const updateQuantity = (productId, newQuantity) => {
    const item = cart.find(i => i.productId === productId);
    
    if (newQuantity > item.maxStock) {
      setMessage({ type: 'warning', text: 'No hay más stock disponible' });
      return;
    }

    if (newQuantity <= 0) {
      removeFromCart(productId);
      return;
    }

    setCart(cart.map(item =>
      item.productId === productId
        ? { ...item, quantity: newQuantity }
        : item
    ));
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter(item => item.productId !== productId));
  };

  const calculateSubtotal = () => {
    return cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  const calculateDiscountAmount = () => {
    const subtotal = calculateSubtotal();
    const discountValue = Math.max(0, parseFloat(checkoutData.discount) || 0);
    
    if (checkoutData.discountType === 'percentage') {
      return (subtotal * Math.min(discountValue, 100)) / 100;
    }
    return Math.min(discountValue, subtotal);
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === 'Enter' && products.length === 1) {
      event.preventDefault();
      addToCart(products[0]);
      setSearch('');
    }
  };

  const calculateTotal = () => {
    return calculateSubtotal() - calculateDiscountAmount();
  };

  const handleCheckout = () => {
    if (cart.length === 0) {
      setMessage({ type: 'warning', text: 'El carrito está vacío' });
      return;
    }
    setOpenCheckout(true);
  };

  const handleCompleteSale = async () => {
    setProcessingPayment(true);
    
    // Si es pago con MercadoPago, usar flujo especial
    if (checkoutData.paymentMethod === 'mercadopago') {
      await handleMercadoPagoPayment();
      return;
    }

    // Pago normal (efectivo, tarjeta, transferencia)
    const saleData = {
      customerName: checkoutData.customerName || null,
      customerDocument: checkoutData.customerDocument || null,
      items: cart.map(item => ({
        productId: item.productId,
        quantity: item.quantity
      })),
      discount: calculateDiscountAmount(),
      paymentMethod: checkoutData.paymentMethod,
      tipoComprobante: checkoutData.tipoComprobante,
      notes: checkoutData.notes || null
    };

    const result = await saleService.create(saleData);
    
    setProcessingPayment(false);

    if (result.success) {
      setMessage({ type: 'success', text: 'Venta registrada correctamente' });
      
      // Guardar venta completa para el recibo
      setCompletedSale(result.data);
      
      // Limpiar carrito y formulario
      setCart([]);
      setCheckoutData({
        customerName: '',
        customerDocument: '',
        customerEmail: '',
        paymentMethod: 'efectivo',
        tipoComprobante: 0, // Recibo/Ticket por defecto
        discount: 0,
        discountType: 'percentage',
        notes: ''
      });
      
      // Cerrar checkout y abrir recibo
      setOpenCheckout(false);
      setOpenReceipt(true);
      
      loadProducts(); // Recargar para actualizar stock
    } else {
      setMessage({ type: 'error', text: result.message });
    }
  };

  const handleMercadoPagoPayment = async () => {
    // Validar datos requeridos para MercadoPago
    if (!checkoutData.customerName || !checkoutData.customerEmail) {
      setMessage({ 
        type: 'warning', 
        text: 'Para pagar con MercadoPago se requiere nombre y email del cliente' 
      });
      setProcessingPayment(false);
      return;
    }

    const customerData = {
      name: checkoutData.customerName,
      email: checkoutData.customerEmail,
      document: checkoutData.customerDocument || ''
    };

    const result = await paymentService.processPaymentWithMercadoPago(
      cart,
      customerData,
      calculateDiscountAmount(),
      checkoutData.notes || ''
    );

    setProcessingPayment(false);

    if (result.success) {
      // Redirigir a MercadoPago
      setMessage({ type: 'info', text: 'Redirigiendo a MercadoPago...' });
      
      // Usar initPoint para producción o sandboxInitPoint para testing
      const paymentUrl = result.data.sandboxInitPoint || result.data.initPoint;
      
      setTimeout(() => {
        window.location.href = paymentUrl;
      }, 1000);
    } else {
      setMessage({ type: 'error', text: result.message });
    }
  };

  // Función para renderizar productos según el modo de vista
  const renderProduct = (product) => {
    const imageUrl = product.image ? uploadService.getImageUrl(product.image) : '/no-image.svg';
    const quantityInCart = cart.find(item => item.productId === product.id)?.quantity || 0;
    const lowStock = Number(product.stock) <= Number(product.minStock || 3);

    // Vista de lista
    if (viewMode === 'list') {
      return (
        <Card
          className={`pos-product-card ${quantityInCart ? 'pos-product-selected' : ''}`}
          sx={{ 
            cursor: 'pointer', 
            '&:hover': { boxShadow: 3 },
            mb: 1
          }}
          onClick={() => addToCart(product)}
        >
          <CardContent>
            <Box className="d-flex align-center gap-md">
              <img
                src={imageUrl}
                alt={product.name}
                style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 4 }}
                onError={(e) => { e.target.src = '/no-image.svg'; }}
              />
              <Box className="flex-1">
                <Typography variant="subtitle1" className="font-semibold">
                  {product.name}
                </Typography>
                {product.code && (
                  <Typography variant="caption" color="text.secondary">
                    Código: {product.code}
                  </Typography>
                )}
                <Typography variant="caption" className={lowStock ? 'pos-low-stock' : ''}>
                  Stock: {product.stock}
                </Typography>
              </Box>
              <Box className="text-right">
                <Typography variant="h6" color="primary" className="font-bold pos-product-price">
                  {formatCurrency(product.price)}
                </Typography>
                {quantityInCart > 0 && <Chip size="small" color="primary" label={`${quantityInCart} en carrito`} />}
              </Box>
            </Box>
          </CardContent>
        </Card>
      );
    }

    // Vista de grilla compacta
    if (viewMode === 'grid-compact') {
      return (
        <Card
          sx={{ 
            cursor: 'pointer', 
            '&:hover': { boxShadow: 3 },
            height: '100%'
          }}
          onClick={() => addToCart(product)}
        >
          <CardContent sx={{ p: 1.5 }}>
            <img
              src={imageUrl}
              alt={product.name}
              style={{ width: '100%', height: 80, objectFit: 'cover', borderRadius: 4, marginBottom: 8 }}
              onError={(e) => { e.target.src = '/no-image.svg'; }}
            />
            <Typography variant="body2" className="font-semibold" sx={{ fontSize: '0.85rem', mb: 0.5 }}>
              {product.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              Stock: {product.stock}
            </Typography>
            <Typography variant="subtitle2" color="primary" className="font-bold">
              {formatCurrency(product.price)}
            </Typography>
          </CardContent>
        </Card>
      );
    }

    // Vista de grilla con imagen grande
    if (viewMode === 'grid-image') {
      return (
        <Card
          sx={{ 
            cursor: 'pointer', 
            '&:hover': { boxShadow: 3 },
            height: '100%'
          }}
          onClick={() => addToCart(product)}
        >
          <img
            src={imageUrl}
            alt={product.name}
            style={{ width: '100%', height: 180, objectFit: 'cover' }}
            onError={(e) => { e.target.src = '/no-image.svg'; }}
          />
          <CardContent>
            <Typography variant="subtitle1" className="font-semibold" sx={{ mb: 1 }}>
              {product.name}
            </Typography>
            <Box className="d-flex justify-between align-center">
              <Typography variant="body2" color="text.secondary">
                Stock: {product.stock}
              </Typography>
              <Typography variant="h6" color="primary" className="font-bold">
                {formatCurrency(product.price)}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      );
    }

    // Vista de grilla grande (por defecto)
    return (
      <Card
        className={`pos-product-card ${quantityInCart ? 'pos-product-selected' : ''}`}
        sx={{ 
          cursor: 'pointer', 
          '&:hover': { boxShadow: 3 },
          height: '100%'
        }}
        onClick={() => addToCart(product)}
      >
        <CardContent>
          <Box className="d-flex align-center gap-sm">
            <img
              src={imageUrl}
              alt={product.name}
              className="img-thumbnail-lg"
              onError={(e) => { e.target.src = '/no-image.svg'; }}
            />
            <Box className="flex-1">
              <Typography variant="subtitle2" className="font-semibold">
                {product.name}
              </Typography>
              <Typography variant="body2" className={lowStock ? 'pos-low-stock' : ''}>
                Stock: {product.stock}{lowStock ? ' · Bajo' : ''}
              </Typography>
              <Typography variant="h6" color="primary" className="pos-product-price">
                {formatCurrency(product.price)}
              </Typography>
            </Box>
            <Box className="pos-product-action">
              {quantityInCart > 0 && <Chip size="small" color="primary" label={quantityInCart} />}
              <IconButton
                color="primary"
                aria-label={`Agregar ${product.name}`}
                onClick={(event) => {
                  event.stopPropagation();
                  addToCart(product);
                }}
              >
                <Add />
              </IconButton>
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box className="admin-page">
      <AdminPageHeader
        eyebrow="CAJA"
        title="Punto de venta"
        subtitle="Buscá productos, armá el pedido y registrá el cobro."
        icon={<ShoppingCart />}
      />

      {message.text && (
        <Alert severity={message.type} onClose={() => setMessage({ type: '', text: '' })} className="mb-md">
          {message.text}
        </Alert>
      )}

      <Grid container spacing={3} className="pos-workspace">
        {/* Productos */}
        <Grid item xs={12} md={7}>
          <Card className="pos-products-panel">
            <CardContent>
              {/* Barra de búsqueda y controles */}
              <Box className="pos-toolbar">
                <TextField
                  size="small"
                  fullWidth
                  className="pos-toolbar-search"
                  placeholder="Buscar producto..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  autoFocus
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: search ? (
                      <InputAdornment position="end">
                        <IconButton size="small" aria-label="limpiar búsqueda" onClick={() => setSearch('')}>
                          <Close fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null
                  }}
                />

                <Typography variant="caption" color="text.secondary" className="pos-toolbar-count">
                  {products.length} prod.
                </Typography>

                <ToggleButtonGroup
                  value={viewMode}
                  exclusive
                  onChange={handleViewModeChange}
                  size="small"
                  aria-label="modo de vista"
                >
                  <ToggleButton value="grid-large" aria-label="grilla grande">
                    <ViewModule fontSize="small" />
                  </ToggleButton>
                  <ToggleButton value="list" aria-label="lista">
                    <ViewList fontSize="small" />
                  </ToggleButton>
                </ToggleButtonGroup>

                <Button
                  size="small"
                  className="pos-toolbar-filters"
                  variant={showFilters || advancedFilterCount > 0 ? 'contained' : 'outlined'}
                  startIcon={showFilters ? <ExpandLess /> : <FilterList />}
                  onClick={() => setShowFilters(!showFilters)}
                >
                  Filtros{advancedFilterCount > 0 ? ` · ${advancedFilterCount}` : ''}
                </Button>
              </Box>

              {/* Tira de categorías */}
              <Box className="pos-category-strip" role="group" aria-label="categorías">
                <Chip
                  size="small"
                  label="Todos"
                  color={filters.category ? 'default' : 'primary'}
                  variant={filters.category ? 'outlined' : 'filled'}
                  onClick={() => handleFilterChange('category', '')}
                />
                {activeCategories.map(category => (
                  <Chip
                    key={category.id}
                    size="small"
                    label={category.name}
                    color={filters.category === category.id ? 'primary' : 'default'}
                    variant={filters.category === category.id ? 'filled' : 'outlined'}
                    onClick={() => handleFilterChange('category', category.id)}
                  />
                ))}
              </Box>

              {/* Panel de filtros colapsable */}
              <Collapse in={showFilters}>
                <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                  <Box className="d-flex justify-between align-center mb-sm">
                    <Typography variant="subtitle2" className="font-semibold">
                      Filtros
                    </Typography>
                    {hasActiveFilters() && (
                      <Button
                        size="small"
                        startIcon={<Clear />}
                        onClick={clearFilters}
                      >
                        Limpiar
                      </Button>
                    )}
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        label="Marca"
                        value={filters.brand}
                        onChange={(e) => handleFilterChange('brand', e.target.value)}
                      >
                        <MenuItem value="">Todas</MenuItem>
                        {brands.map((brand) => (
                          <MenuItem key={brand.id} value={brand.id}>
                            {brand.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Stock mínimo"
                        value={filters.minStock}
                        onChange={(e) => handleFilterChange('minStock', parseInt(e.target.value) || 0)}
                        inputProps={{ min: 0 }}
                      />
                    </Grid>

                    <Grid item xs={6} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Precio mínimo"
                        value={filters.minPrice}
                        onChange={(e) => handleFilterChange('minPrice', e.target.value)}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>
                        }}
                      />
                    </Grid>

                    <Grid item xs={6} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Precio máximo"
                        value={filters.maxPrice}
                        onChange={(e) => handleFilterChange('maxPrice', e.target.value)}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>
                        }}
                      />
                    </Grid>
                  </Grid>

                  {/* Chips de filtros activos */}
                  {advancedFilterCount > 0 && (
                    <Box className="d-flex gap-xs mt-sm" sx={{ flexWrap: 'wrap' }}>
                      {filters.brand && (
                        <Chip
                          size="small"
                          label={`Marca: ${brands.find(b => b.id === filters.brand)?.name}`}
                          onDelete={() => handleFilterChange('brand', '')}
                        />
                      )}
                      {filters.minPrice && (
                        <Chip
                          size="small"
                          label={`Desde: $${filters.minPrice}`}
                          onDelete={() => handleFilterChange('minPrice', '')}
                        />
                      )}
                      {filters.maxPrice && (
                        <Chip
                          size="small"
                          label={`Hasta: $${filters.maxPrice}`}
                          onDelete={() => handleFilterChange('maxPrice', '')}
                        />
                      )}
                      {filters.minStock > 0 && (
                        <Chip
                          size="small"
                          label={`Stock min: ${filters.minStock}`}
                          onDelete={() => handleFilterChange('minStock', 0)}
                        />
                      )}
                    </Box>
                  )}
                </Paper>
              </Collapse>

              {/* Lista de productos */}
              {loading ? (
                <Box className="d-flex justify-center align-center min-h-60vh">
                  <CircularProgress />
                </Box>
              ) : products.length === 0 ? (
                <Box className="text-center py-lg">
                  <Typography color="text.secondary">
                    No se encontraron productos
                  </Typography>
                </Box>
              ) : (
                <Box className="pos-products-scroll">
                  {viewMode === 'list' ? (
                    <Box>
                      {products.map((product) => (
                        <Box key={product.id}>
                          {renderProduct(product)}
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Grid container spacing={2}>
                      {products.map((product) => (
                        <Grid 
                          item 
                          xs={12} 
                          sm={viewMode === 'grid-compact' ? 4 : viewMode === 'grid-image' ? 6 : 6}
                          key={product.id}
                        >
                          {renderProduct(product)}
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Carrito */}
        <Grid item xs={12} md={5} className="pos-cart-desktop">
          <Card className="pos-cart-panel">
            <CardContent className="pos-cart-content">
              <Box className="pos-cart-header">
                <Box>
                  <Typography variant="h6" className="font-semibold">
                    Carrito
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {cart.reduce((sum, item) => sum + item.quantity, 0)} unidades
                  </Typography>
                </Box>
                {cart.length > 0 && (
                  <Button size="small" color="error" onClick={() => setCart([])}>
                    Vaciar
                  </Button>
                )}
              </Box>

              {cart.length === 0 ? (
                <Box className="pos-cart-empty">
                  <ShoppingCart sx={{ fontSize: 52, color: 'grey.400', mb: 1 }} />
                  <Typography color="text.secondary">
                    El carrito está vacío
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Tocá un producto o buscá por código para agregarlo.
                  </Typography>
                </Box>
              ) : (
                <>
                  <Box className="pos-cart-items">
                    {cart.map((item) => (
                      <Box key={item.productId} className="pos-cart-item">
                        <Box className="d-flex justify-between align-center mb-xs">
                          <Box>
                            <Typography variant="subtitle2" className="font-semibold">{item.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatCurrency(item.price)} c/u
                            </Typography>
                          </Box>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => removeFromCart(item.productId)}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Box>
                        <Box className="d-flex justify-between align-center">
                          <Box className="d-flex align-center gap-xs">
                            <IconButton
                              size="small"
                              className="pos-quantity-button"
                              onClick={() => updateQuantity(item.productId, item.quantity - 1)}
                            >
                              <Remove fontSize="small" />
                            </IconButton>
                            <Typography className="pos-quantity-value">{item.quantity}</Typography>
                            <IconButton
                              size="small"
                              className="pos-quantity-button"
                              onClick={() => updateQuantity(item.productId, item.quantity + 1)}
                            >
                              <Add fontSize="small" />
                            </IconButton>
                          </Box>
                          <Typography variant="subtitle2" className="pos-line-total">
                            {formatCurrency(item.price * item.quantity)}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>

                  <Box className="pos-cart-summary">
                    <Box className="d-flex justify-between align-center mb-sm">
                      <Typography color="text.secondary">Subtotal</Typography>
                      <Typography variant="h5" className="font-bold">
                        {formatCurrency(calculateSubtotal())}
                      </Typography>
                    </Box>
                    <Button
                      fullWidth
                      variant="contained"
                      size="large"
                      startIcon={<Receipt />}
                      onClick={handleCheckout}
                      className="pos-checkout-button"
                    >
                      Cobrar {formatCurrency(calculateSubtotal())}
                    </Button>
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box className="pos-mobile-cart-bar">
        <Button
          className="pos-mobile-cart-review"
          onClick={() => setMobileCartOpen(true)}
          aria-label="Revisar carrito"
        >
          <Box className="pos-mobile-cart-count">
            <ShoppingCart />
            <span>{cart.reduce((sum, item) => sum + item.quantity, 0)}</span>
          </Box>
          <Box className="pos-mobile-cart-label">
            <strong>{cart.length ? 'Ver carrito' : 'Carrito vacío'}</strong>
            <small>{cart.length ? `${cart.length} productos` : 'Agregá productos para comenzar'}</small>
          </Box>
        </Button>
        <Button
          className="pos-mobile-checkout"
          variant="contained"
          disabled={cart.length === 0}
          onClick={handleCheckout}
        >
          <small>Cobrar</small>
          <strong>{formatCurrency(calculateSubtotal())}</strong>
        </Button>
      </Box>

      <Drawer
        anchor="bottom"
        open={mobileCartOpen}
        onClose={() => setMobileCartOpen(false)}
        PaperProps={{ className: 'pos-mobile-cart-drawer' }}
      >
        <Box className="pos-mobile-cart-handle" />
        <Box className="pos-mobile-cart-header">
          <Box>
            <Typography variant="h6" className="font-semibold">Tu carrito</Typography>
            <Typography variant="caption" color="text.secondary">
              {cart.reduce((sum, item) => sum + item.quantity, 0)} unidades
            </Typography>
          </Box>
          <IconButton onClick={() => setMobileCartOpen(false)} aria-label="Cerrar carrito">
            <Close />
          </IconButton>
        </Box>

        {cart.length === 0 ? (
          <Box className="pos-mobile-cart-empty">
            <ShoppingCart />
            <Typography>El carrito está vacío</Typography>
            <Button variant="outlined" onClick={() => setMobileCartOpen(false)}>
              Seguir eligiendo
            </Button>
          </Box>
        ) : (
          <>
            <Box className="pos-mobile-cart-items">
              {cart.map(item => (
                <Box key={item.productId} className="pos-cart-item">
                  <Box className="d-flex justify-between align-start mb-xs">
                    <Box>
                      <Typography variant="subtitle2" className="font-semibold">{item.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatCurrency(item.price)} c/u
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => removeFromCart(item.productId)}
                      aria-label={`Quitar ${item.name}`}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                  <Box className="d-flex justify-between align-center">
                    <Box className="d-flex align-center gap-xs">
                      <IconButton
                        size="small"
                        className="pos-quantity-button"
                        onClick={() => updateQuantity(item.productId, item.quantity - 1)}
                      >
                        <Remove fontSize="small" />
                      </IconButton>
                      <Typography className="pos-quantity-value">{item.quantity}</Typography>
                      <IconButton
                        size="small"
                        className="pos-quantity-button"
                        onClick={() => updateQuantity(item.productId, item.quantity + 1)}
                      >
                        <Add fontSize="small" />
                      </IconButton>
                    </Box>
                    <Typography className="pos-line-total">
                      {formatCurrency(item.price * item.quantity)}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
            <Box className="pos-mobile-cart-summary">
              <Box className="d-flex justify-between align-center">
                <Typography color="text.secondary">Total</Typography>
                <Typography variant="h5" className="font-bold">
                  {formatCurrency(calculateSubtotal())}
                </Typography>
              </Box>
              <Button
                fullWidth
                variant="contained"
                size="large"
                startIcon={<Receipt />}
                onClick={() => {
                  setMobileCartOpen(false);
                  handleCheckout();
                }}
                className="pos-checkout-button"
              >
                Cobrar
              </Button>
            </Box>
          </>
        )}
      </Drawer>

      {/* Dialog de checkout */}
      <Dialog open={openCheckout} onClose={() => setOpenCheckout(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Completar Venta</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} className="mt-xs">
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nombre del Cliente (opcional)"
                value={checkoutData.customerName}
                onChange={(e) => setCheckoutData({ ...checkoutData, customerName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="DNI/CUIT del Cliente (opcional)"
                value={checkoutData.customerDocument}
                onChange={(e) => setCheckoutData({ ...checkoutData, customerDocument: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Método de Pago"
                value={checkoutData.paymentMethod}
                onChange={(e) => setCheckoutData({ ...checkoutData, paymentMethod: e.target.value })}
              >
                <MenuItem value="efectivo">Efectivo</MenuItem>
                <MenuItem value="tarjeta">Tarjeta</MenuItem>
                <MenuItem value="transferencia">Transferencia</MenuItem>
                <MenuItem value="mercadopago">MercadoPago</MenuItem>
              </TextField>
            </Grid>
            
            {/* El tipo de comprobante es fijo como Recibo/Ticket en el POS */}
            {/* Para facturación fiscal, se genera posteriormente desde el módulo de Facturación */}
            
            {/* Campo de email requerido para MercadoPago */}
            {checkoutData.paymentMethod === 'mercadopago' && (
              <Grid item xs={12}>
                <Alert severity="info" className="mb-xs">
                  MercadoPago requiere el nombre y email del cliente
                </Alert>
                <TextField
                  fullWidth
                  label="Email del Cliente *"
                  type="email"
                  value={checkoutData.customerEmail}
                  onChange={(e) => setCheckoutData({ ...checkoutData, customerEmail: e.target.value })}
                  required
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <Typography variant="subtitle2" className="mb-xs">
                Descuento
              </Typography>
              <Box className="d-flex gap-sm">
                <TextField
                  select
                  value={checkoutData.discountType}
                  onChange={(e) => setCheckoutData({ ...checkoutData, discountType: e.target.value })}
                  sx={{ width: 140 }}
                >
                  <MenuItem value="percentage">Porcentaje (%)</MenuItem>
                  <MenuItem value="fixed">Monto Fijo ($)</MenuItem>
                </TextField>
                <TextField
                  fullWidth
                  type="number"
                  placeholder={checkoutData.discountType === 'percentage' ? 'Ej: 10' : 'Ej: 1000'}
                  value={checkoutData.discount}
                  onChange={(e) => setCheckoutData({ ...checkoutData, discount: e.target.value })}
                  inputProps={{ 
                    min: 0,
                    max: checkoutData.discountType === 'percentage' ? 100 : undefined,
                    step: checkoutData.discountType === 'percentage' ? 1 : 0.01
                  }}
                />
              </Box>
              {checkoutData.discount > 0 && (
                <Typography variant="caption" color="text.secondary" className="mt-xs">
                  Descuento aplicado: {formatCurrency(calculateDiscountAmount())}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notas (opcional)"
                multiline
                rows={2}
                value={checkoutData.notes}
                onChange={(e) => setCheckoutData({ ...checkoutData, notes: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <Divider />
              <Box className="mt-md">
                <Box className="d-flex justify-between mb-xs">
                  <Typography>Subtotal:</Typography>
                  <Typography>{formatCurrency(calculateSubtotal())}</Typography>
                </Box>
                {checkoutData.discount > 0 && (
                  <Box className="d-flex justify-between mb-xs">
                    <Typography color="success.main">
                      Descuento ({checkoutData.discountType === 'percentage' ? `${checkoutData.discount}%` : 'Monto fijo'}):
                    </Typography>
                    <Typography color="success.main">
                      -{formatCurrency(calculateDiscountAmount())}
                    </Typography>
                  </Box>
                )}
                <Divider className="my-sm" />
                <Box className="d-flex justify-between">
                  <Typography variant="h5" className="font-bold">Total a Cobrar:</Typography>
                  <Typography variant="h5" color="primary" className="font-bold">
                    {formatCurrency(calculateTotal())}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCheckout(false)} disabled={processingPayment}>
            Cancelar
          </Button>
          <Button
            onClick={handleCompleteSale}
            variant="contained"
            disabled={processingPayment}
            startIcon={processingPayment ? <CircularProgress size={20} /> : <Receipt />}
          >
            {processingPayment ? 'Procesando...' : 'Confirmar Venta'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Recibo */}
      <ReceiptModal
        open={openReceipt}
        onClose={() => setOpenReceipt(false)}
        sale={completedSale}
      />
    </Box>
  );
};

export default POS;

